import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import React, { createContext, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { urls } from '../routers/urls';
import { setLogoutHandler } from './forceLogout';
import { roles } from './roles';

interface KindeContextType {
  isAuthenticated: boolean;
  user: any;
  loading: boolean;
  login(): Promise<void>;
  logout(): Promise<void>;
  getAccessToken(): Promise<string | undefined>;
}

export const KindeContext = createContext<KindeContextType | null>(null);
export const useKinde = () => useContext(KindeContext)!;

// Global token storage for axios interceptor
let accessToken: string | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export const KindeWrapper = ({ children }): any => {
  const {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken: kindeGetAccessToken,
    getClaim,
  } = useKindeAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const [enrichedUser, setEnrichedUser] = React.useState<any>(null);
  const [isLoggingOut, setIsLoggingOut] = React.useState<boolean>(false);
  const [hasInitializedAuth, setHasInitializedAuth] = React.useState<boolean>(false);

  // Wait for Kinde SDK to fully initialize before checking auth state
  useEffect(() => {
    if (!isLoading) {
      // SDK finished loading, mark as initialized
      setHasInitializedAuth(true);
    }
  }, [isLoading]);

  // Auto-redirect if not authenticated (but not during logout or on auth pages)
  useEffect(() => {
    // Don't do anything until SDK has fully initialized
    if (!hasInitializedAuth) {
      return;
    }

    const currentPath = location.pathname;
    const isAuthPage = currentPath === urls.authentication || currentPath === urls.callback;

    // Don't redirect if we're waiting for user enrichment (authenticated but enrichedUser not ready)
    const isWaitingForEnrichment = isAuthenticated && !enrichedUser;

    if (!isAuthenticated && !isLoggingOut && !isAuthPage && !isWaitingForEnrichment) {
      // Save the current URL (including search params) so we can redirect back after login
      // Use sessionStorage since location state is lost during external Kinde redirect
      const from = location.pathname + location.search;
      sessionStorage.setItem('auth_redirect_url', from);
      navigate(urls.authentication);
    }
  }, [hasInitializedAuth, isAuthenticated, isLoggingOut, navigate, location.pathname, location.search, enrichedUser]);

  // Enrich user object with claims
  useEffect(() => {
    const enrichUser = async () => {
      if (isAuthenticated && user) {
        try {
          // Get claims
          const rolesClaim = await getClaim('roles');
          const externalOrgIdClaim = await getClaim('external_org_id');

          // Extract role from Kinde format - find highest authority role
          const roleValues = Object.values(roles);
          const roleName = (rolesClaim?.value as any[] | undefined)
            ?.map(item => (typeof item === 'object' && item?.key ? item.key : item) as string)
            .filter(key => roleValues.includes(key as roles))
            .reduce<string | null>((highest, key) => {
              if (!highest) return key;
              return roleValues.indexOf(key as roles) > roleValues.indexOf(highest as roles) ? key : highest;
            }, null);

          setEnrichedUser({
            ...user,
            external_org_id: externalOrgIdClaim?.value || null,
            role: roleName,
          });
        } catch (error) {
          console.error('[Kinde] Failed to enrich user:', error);
          setEnrichedUser(user);
        }
      }
    };

    enrichUser();
  }, [isAuthenticated, user, getClaim]);

  // Update token in memory when authenticated
  useEffect(() => {
    const updateToken = async () => {
      if (isAuthenticated && kindeGetAccessToken) {
        try {
          const token = await kindeGetAccessToken();
          setAccessToken(token || null);
        } catch (err) {
          console.error('[Auth] Failed to get access token:', err);
          setAccessToken(null);
        }
      } else {
        setAccessToken(null);
      }
    };
    updateToken();
  }, [isAuthenticated, kindeGetAccessToken]);

  const handleLogin = async () => {
    try {
      const { config } = await import('../config');
      await login(config.auth.org_code ? { orgCode: config.auth.org_code } : {});
    } catch (error) {
      console.error('[Auth] Login error:', error);
    }
  };

  const handleLogout = async () => {
    try {
      // Set logging out flag to prevent auto-redirect
      setIsLoggingOut(true);

      // Clear localStorage but preserve language preference
      const currentLang = localStorage.getItem('i18nextLng');
      localStorage.clear();
      if (currentLang) {
        localStorage.setItem('i18nextLng', currentLang);
      }

      // Clear token from memory
      setAccessToken(null);

      // Kinde logout (will redirect to logout_uri)
      await logout();
    } catch (error) {
      console.error('[Auth] Logout error:', error);
      setIsLoggingOut(false);
    }
  };

  // Lets the axios interceptor log out a suspended user (see forceLogout)
  useEffect(() => {
    setLogoutHandler(handleLogout);
    return () => setLogoutHandler(null);
  });

  // Show loading while waiting for user enrichment
  if (isAuthenticated && !enrichedUser) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontSize: '16px',
          color: '#666',
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <KindeContext.Provider
      value={{
        isAuthenticated,
        user: enrichedUser || user, // Use enriched user with roles and org info
        loading: isLoading,
        login: handleLogin,
        logout: handleLogout,
        getAccessToken: kindeGetAccessToken,
      }}
    >
      {children}
    </KindeContext.Provider>
  );
};
